import { cloneDeep, isBlank } from '@shared/object'
import { getCurrentPage, router } from '@shared/inertia'

// Public: The query parameters of the current URL.
export function currentParams () {
  if (import.meta.env.SSR) return new URLSearchParams()

  return new URLSearchParams(location.search)
}

interface NavigationOptions {
  replace?: boolean
  pathname?: string
  component?: string
}

// Public: Allows to push a new URL with new query parameters.
export function pushQuery (queryParams: Record<string, any>, options?: NavigationOptions) {
  const params = currentParams()
  const oldParams = params.toString()
  Object.entries(queryParams).forEach(([name, value]) => {
    isBlank(value) ? params.delete(name) : params.set(name, value)
  })
  const newParams = params.toString()

  if (newParams !== oldParams) {
    const expectedPathname = options?.pathname || location.pathname
    const newUrl = `${expectedPathname}?${newParams}`

    // Update the browser URL synchronously via `window.history` instead of
    // `router.push`/`router.replace`. The router methods (a) fire
    // `inertia:navigate`, which `ModalsManager` listens to and uses to
    // close all open modals — so a modal that calls `pushQuery` from its
    // own `onMounted` would flicker shut, and (b) are async (queued
    // through Inertia's `clientVisitQueue`), so a follow-up `pushQuery`
    // reads a stale `location.pathname` and ends up writing back the
    // *previous* page's path. We preserve Inertia's history-state shape
    // (`{...state, page: {..., url}}`) so the browser back/forward →
    // `popstate` handler still restores correctly. `replace` vs `push`
    // matters because some flows (e.g. GiftWizardModal.close) call
    // `history.back()` and expect the previous page entry to be there.
    const oldState = window.history.state
    const newState = oldState?.page
      ? { ...oldState, page: { ...oldState.page, url: newUrl } }
      : oldState
    window.history[options?.replace ? 'replaceState' : 'pushState'](newState, '', newUrl)

    // Inertia v3 enqueues async history operations during page mount (via
    // `Promise.resolve().then` chains) using its internally-tracked page URL
    // (the URL the server returned, without our query string). If we update
    // the URL synchronously *before* Inertia's queue drains, the queued
    // `doPushState` runs later and clobbers our URL. The clobber fires from
    // a chain of macrotasks after page mount, so a single `setTimeout(0)`
    // isn't enough. We re-check at a few points within the first ~250ms,
    // which is long enough to win against Inertia's initial-mount queue but
    // short enough that the listener has expired by the time the user
    // could navigate (history.back / Inertia visit) — important because we
    // must not re-apply our URL on top of a deliberate user navigation.
    const expectedSearch = `?${newParams}`
    const reapply = () => {
      if (location.pathname === expectedPathname && location.search !== expectedSearch)
        window.history.replaceState(window.history.state, '', newUrl)
    }
    setTimeout(reapply, 0)
    setTimeout(reapply, 50)
    setTimeout(reapply, 250)
  }
}

// Public: Allows to add/update/remove a query parameter in the current URL.
export function replaceQuery (queryParams: Record<string, any>) {
  return pushQuery(queryParams, { replace: true })
}

// Public: Allows to update state in the current page that should persist
// across navigations. Goes through `router.replace` (with `preserveState`)
// so Vue's reactive `usePage()` sees the new props — components watching
// `page.props.X` (e.g. the corporate search composable's `useSearchData`)
// rely on this. `preserveState: true` skips the remount so we don't re-run
// `onMounted` hooks. Inertia stores the resulting page in browser history
// state automatically, so back/forward restore replays the saved attrs.
//
// Passing `url: location.href` is critical: without it, `router.replace`
// uses Inertia's tracked page URL (the URL at the last full Inertia visit),
// which clobbers any query-string updates we wrote synchronously via
// `pushQuery`/`history.replaceState`. The corporate search flow updates the
// URL with filter/sort params via `replaceQuery` BEFORE calling
// `saveInertiaState`, so the search filters end up in subsequent request
// `Referer` headers — which `redirect_back` (e.g. after deleting a row)
// depends on to return to the filtered list.
export function saveInertiaState (attrs: any) {
  if (!getCurrentPage()) return

  router.replace({
    url: location.href,
    props: props => ({ ...props, ...cloneDeep(attrs) }),
    preserveState: true,
    preserveScroll: true,
  })
}

export function makeAbsoluteUrl (path: string) {
  return new URL(path, document.baseURI).href
}
